import IMask from 'imask';
import intlTelInput from 'intl-tel-input';

const root = document.querySelector('.wb-order-page');
const deliveryMapConfigUrl = new URL('../../data/order-delivery-map.json', import.meta.url);
const deliveryMapCdekIconUrl = new URL('../../images/svg/delivery/delivery-cdek-map.svg', import.meta.url);
let deliveryMapApiPromise = null;


const normalizeCountryIso2 = (value) => {
  if (typeof value !== 'string') return '';
  const country = value.trim().toLowerCase();
  return /^[a-z]{2}$/.test(country) ? country : '';
};

const getCountryFromBrowserLocale = () => {
  const locales = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : [navigator.language];

  for (const locale of locales) {
    const match = /[-_]([a-z]{2})\b/i.exec(locale || '');
    const country = normalizeCountryIso2(match?.[1]);
    if (country) return country;
  }

  return 'ru';
};

const getInitialPhoneCountry = () => {
  const customLookup = window.WB_ORDER_PHONE_COUNTRY_LOOKUP || window.wbOrderPhoneCountryLookup;

  if (typeof customLookup === 'function') {
    return Promise.resolve(customLookup())
      .then((country) => normalizeCountryIso2(country) || getCountryFromBrowserLocale())
      .catch(() => getCountryFromBrowserLocale());
  }

  const lookupUrl = document.documentElement.dataset.wbPhoneCountryLookupUrl || window.WB_ORDER_PHONE_COUNTRY_LOOKUP_URL;

  if (lookupUrl && typeof fetch === 'function') {
    return fetch(lookupUrl, { credentials: 'omit' })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Country lookup failed'))))
      .then((data) => normalizeCountryIso2(data.country_code || data.countryCode || data.country || data.iso2) || getCountryFromBrowserLocale())
      .catch(() => getCountryFromBrowserLocale());
  }

  return Promise.resolve(getCountryFromBrowserLocale());
};

const getPhoneDigits = (value) => value.replace(/\D/g, '');

const getPhoneInstance = (input) => input._wbOrderPhoneInput || null;

const syncPhoneMeta = (input) => {
  const phoneInput = getPhoneInstance(input);
  if (!phoneInput || typeof phoneInput.getSelectedCountryData !== 'function') return;

  const country = phoneInput.getSelectedCountryData();
  if (!country) return;

  input.dataset.wbOrderPhoneCountry = country.iso2 || '';
  input.dataset.wbOrderPhoneDialCode = country.dialCode || '';
};

const getFieldControl = (field) => field.querySelector('input:not([type="hidden"]):not([data-wb-order-native-date]), textarea');

const setFieldFilledState = (field) => {
  const control = getFieldControl(field);
  if (!control) return;
  field.classList.toggle('is-filled', Boolean(control.value.trim()));
};

const isRequiredControlFilled = (control) => {
  if (control.type === 'checkbox' || control.type === 'radio') {
    const form = control.closest('[data-wb-order-form]') || document;
    return Boolean(form.querySelector(`[name="${CSS.escape(control.name)}"]:checked`));
  }

  return Boolean(control.value.trim());
};

const isTextControlValid = (control) => {
  if (control.hasAttribute('required') && !isRequiredControlFilled(control)) {
    return false;
  }

  const isPhone = control.hasAttribute('data-wb-order-phone');
  const minLength = Number(control.dataset.wbOrderMinLength || 0);

  if (isPhone) {
    const digitsLength = getPhoneDigits(control.value).length;
    if (minLength > 0 && control.value.trim().length > 0 && digitsLength < minLength) {
      return false;
    }

    const phoneInput = getPhoneInstance(control);
    if (phoneInput && typeof phoneInput.isValidNumber === 'function' && control.dataset.wbOrderPhoneUtilsReady === 'true') {
      return phoneInput.isValidNumber();
    }
  } else if (minLength > 0 && control.value.trim().length > 0 && control.value.trim().length < minLength) {
    return false;
  }

  if (control.value.trim() && typeof control.checkValidity === 'function' && !control.checkValidity()) {
    return false;
  }

  return true;
};

const markControlValidity = (control, shouldShowError = false) => {
  const field = control.closest('[data-wb-order-field]');
  if (!field) return true;

  const isValid = isTextControlValid(control);
  field.classList.toggle('is-invalid', shouldShowError && !isValid);
  return isValid;
};

const formatDateFromNativeValue = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return '';

  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
};

const formatDateToNativeValue = (value) => {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value.trim());
  if (!match) return '';

  const [, day, month, year] = match;
  return `${year}-${month}-${day}`;
};

const dispatchControlUpdate = (control) => {
  control.dispatchEvent(new Event('input', { bubbles: true }));
  control.dispatchEvent(new Event('change', { bubbles: true }));
};

const openNativeDatePicker = (input) => {
  try {
    if (typeof input.showPicker === 'function') {
      input.focus({ preventScroll: true });
      input.showPicker();
      return;
    }
  } catch (error) {
    // В некоторых браузерах showPicker может быть недоступен для скрытого поля.
  }

  input.focus({ preventScroll: true });
  input.click();
};

const initFields = () => {
  document.querySelectorAll('[data-wb-order-field]').forEach((field) => {
    const control = getFieldControl(field);
    if (!control) return;

    setFieldFilledState(field);
    markControlValidity(control, false);

    control.addEventListener('input', () => {
      setFieldFilledState(field);
      markControlValidity(control, field.classList.contains('is-invalid'));
    });

    control.addEventListener('blur', () => {
      markControlValidity(control, Boolean(control.value.trim()));
    });
  });

  document.querySelectorAll('[data-wb-order-phone]').forEach((input) => {
    const phoneInput = intlTelInput(input, {
      containerClass: 'wb-order-phone-widget',
      initialCountry: '',
      initialCountryLookup: getInitialPhoneCountry,
      formatAsYouType: true,
      strictMode: true,
      separateDialCode: false,
      loadUtils: () => import('intl-tel-input/utils')
    });

    input._wbOrderPhoneInput = phoneInput;
    syncPhoneMeta(input);

    const utilsReady = phoneInput.promise?.then(() => {
      input.dataset.wbOrderPhoneUtilsReady = 'true';
      syncPhoneMeta(input);
      dispatchControlUpdate(input);
    }).catch(() => {
      input.dataset.wbOrderPhoneUtilsReady = 'false';
      dispatchControlUpdate(input);
    });

    if (!utilsReady) {
      input.dataset.wbOrderPhoneUtilsReady = 'false';
    }

    input.addEventListener('countrychange', () => {
      syncPhoneMeta(input);
      dispatchControlUpdate(input);
    });

    input.addEventListener('blur', () => {
      syncPhoneMeta(input);
    });
  });

  document.querySelectorAll('input[name="birthday"]').forEach((input) => {
    IMask(input, {
      mask: '00.00.0000'
    });
  });

  document.querySelectorAll('[data-wb-order-field]').forEach((field) => {
    const input = field.querySelector('[data-wb-order-date-input]');
    const nativeInput = field.querySelector('[data-wb-order-native-date]');
    const button = field.querySelector('[data-wb-order-date-focus]');

    if (!input || !nativeInput || !button) return;

    nativeInput.max = new Date().toISOString().slice(0, 10);

    const syncNativeInputValue = () => {
      nativeInput.value = formatDateToNativeValue(input.value);
    };

    nativeInput.addEventListener('pointerdown', syncNativeInputValue);
    nativeInput.addEventListener('click', syncNativeInputValue);
    nativeInput.addEventListener('focus', syncNativeInputValue);

    nativeInput.addEventListener('change', () => {
      const formattedDate = formatDateFromNativeValue(nativeInput.value);
      if (!formattedDate) return;

      input.value = formattedDate;
      setFieldFilledState(field);
      markControlValidity(input, field.classList.contains('is-invalid'));
      dispatchControlUpdate(input);
      input.focus();
    });

    button.addEventListener('click', (event) => {
      event.preventDefault();
      syncNativeInputValue();
      openNativeDatePicker(nativeInput);
    });
  });
};

const closeSelect = (select) => {
  const button = select.querySelector('.wb-order-select__button');
  select.classList.remove('is-open');
  if (button) button.setAttribute('aria-expanded', 'false');
};

const initSelects = () => {
  document.querySelectorAll('[data-wb-order-select]').forEach((select) => {
    const button = select.querySelector('.wb-order-select__button');
    const valueNode = button?.querySelector('span');
    const hidden = select.querySelector('input[type="hidden"]');

    button?.addEventListener('click', (event) => {
      event.preventDefault();
      const isOpen = select.classList.contains('is-open');
      document.querySelectorAll('[data-wb-order-select].is-open').forEach(closeSelect);
      select.classList.toggle('is-open', !isOpen);
      button.setAttribute('aria-expanded', String(!isOpen));
    });

    select.querySelectorAll('[data-value]').forEach((option) => {
      option.addEventListener('click', (event) => {
        event.preventDefault();
        const value = option.dataset.value || option.textContent.trim();
        if (valueNode) valueNode.textContent = value;
        if (hidden) {
          hidden.value = value;
          hidden.dispatchEvent(new Event('input', { bubbles: true }));
        }
        closeSelect(select);
      });
    });
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-wb-order-select]')) {
      document.querySelectorAll('[data-wb-order-select].is-open').forEach(closeSelect);
    }
  });
};

const updateRadioCards = (selector) => {
  document.querySelectorAll(selector).forEach((card) => {
    const input = card.querySelector('input[type="radio"]');
    card.classList.toggle('is-active', Boolean(input?.checked));
  });
};

const initRadioCards = () => {
  const groups = [
    '[data-wb-delivery-card]',
    '[data-wb-payment-option]',
    '.wb-order-rate'
  ];

  groups.forEach(updateRadioCards);

  document.addEventListener('change', (event) => {
    if (!event.target.matches('input[type="radio"]')) return;
    groups.forEach(updateRadioCards);
  });
};

const initRegionConfirm = () => {
  document.querySelectorAll('[data-wb-region-answer]').forEach((button) => {
    button.addEventListener('click', () => {
      const group = button.closest('.wb-order-region-confirm');
      group?.querySelectorAll('[data-wb-region-answer]').forEach((item) => {
        item.classList.toggle('is-active', item === button);
      });
    });
  });
};

const updateNextButton = (formRoot) => {
  const next = formRoot.querySelector('[data-wb-order-next]');
  if (!next) return;

  const requiredControls = Array.from(formRoot.querySelectorAll('[data-wb-order-required]'));
  if (!requiredControls.length) return;

  const isValid = requiredControls.every((control) => {
    if (control.matches('input:not([type="hidden"]), textarea')) {
      return isTextControlValid(control);
    }

    return isRequiredControlFilled(control);
  });

  next.classList.toggle('is-disabled', !isValid);

  if (next instanceof HTMLButtonElement) {
    next.disabled = !isValid;
  }
};

const initStepValidation = () => {
  document.querySelectorAll('[data-wb-order-form]').forEach((formRoot) => {
    updateNextButton(formRoot);
    formRoot.addEventListener('input', () => updateNextButton(formRoot));
    formRoot.addEventListener('change', () => updateNextButton(formRoot));

    const form = formRoot.matches('form') ? formRoot : formRoot.querySelector('form');
    form?.addEventListener('submit', (event) => {
      const controls = Array.from(formRoot.querySelectorAll('input:not([type="hidden"]):not([data-wb-order-native-date]), textarea'));
      const isValid = controls.every((control) => markControlValidity(control, true));

      if (!isValid) {
        event.preventDefault();
        updateNextButton(formRoot);
      }
    });
  });
};

const initCollapses = () => {
  document.querySelectorAll('[data-wb-order-collapse]').forEach((button) => {
    const target = document.querySelector(button.dataset.wbOrderCollapse);
    if (!target) return;

    button.addEventListener('click', () => {
      const isCollapsed = target.hidden;
      target.hidden = !isCollapsed;
      button.setAttribute('aria-expanded', String(isCollapsed));
    });
  });
};


const isMobilePointModal = () => window.matchMedia('(max-width: 767.98px)').matches;

const setPointModalView = (modal, view) => {
  const nextView = view === 'list' ? 'list' : 'map';
  modal.dataset.wbPointViewCurrent = nextView;
  modal.classList.toggle('is-map-view', nextView === 'map');
  modal.classList.toggle('is-list-view', nextView === 'list');
  modal.classList.remove('is-detail-view');
  document.body.classList.remove('wb-point-modal-detail-open');
  modal.querySelector('[data-wb-point-detail]')?.setAttribute('aria-hidden', 'true');

  modal.querySelectorAll('[data-wb-point-view]').forEach((button) => {
    const isActive = button.dataset.wbPointView === nextView;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-selected', String(isActive));
  });

  placePointFilters(modal);
};

const placePointFilters = (modal) => {
  const filters = modal.querySelector('[data-wb-point-filters]');
  const anchor = modal.querySelector('[data-wb-point-filters-anchor]');
  const mapShell = modal.querySelector('[data-wb-yandex-map="delivery-points"]');

  if (!filters || !anchor || !mapShell) return;

  const shouldPlaceOnMap = modal.classList.contains('is-map-view') && !isMobilePointModal();

  if (shouldPlaceOnMap) {
    if (filters.parentElement !== mapShell) {
      mapShell.appendChild(filters);
    }
    return;
  }

  if (filters.previousElementSibling !== anchor) {
    anchor.after(filters);
  }
};

const getPointModalInstance = (modal) => {
  if (!(modal instanceof Element) || !window.bootstrap?.Modal) return null;

  try {
    return window.bootstrap.Modal.getOrCreateInstance(modal, {
      backdrop: true,
      focus: true,
      keyboard: true
    });
  } catch (error) {
    return null;
  }
};

const openPointModal = (modal, view = 'map') => {
  setPointModalView(modal, view);
  modal.classList.remove('is-filter-open', 'is-map-point-open');
  document.body.classList.add('wb-point-modal-open');
  modal.querySelectorAll('[data-wb-point-filter-toggle]').forEach((button) => {
    button.setAttribute('aria-expanded', 'false');
  });

  const instance = getPointModalInstance(modal);
  if (instance) {
    instance.show();
    requestYandexMapRender(modal);
    return;
  }

  modal.classList.add('show');
  modal.removeAttribute('aria-hidden');
  modal.style.display = 'block';
  document.body.classList.add('modal-open');
  requestYandexMapRender(modal);
};

const closePointModal = (modal) => {
  const instance = getPointModalInstance(modal);
  if (instance) {
    instance.hide();
    return;
  }

  modal.classList.remove('show');
  modal.setAttribute('aria-hidden', 'true');
  modal.style.display = 'none';
  document.body.classList.remove('modal-open', 'wb-point-modal-open', 'wb-point-modal-detail-open');
};

const getYandexMapApiKey = (config = {}) => (
  config.apiKey
  || window.WB_DELIVERY_MAP_API_KEY
  || window.WB_YANDEX_MAPS_API_KEY
  || document.documentElement.dataset.wbYandexMapsApiKey
  || ''
).trim();

const getYandexMapConfigUrl = (shell) => shell.dataset.wbYandexMapConfig || deliveryMapConfigUrl.toString();

const fetchYandexMapConfig = (shell) => fetch(getYandexMapConfigUrl(shell), { credentials: 'same-origin' })
  .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Delivery map config failed'))))
  .catch(() => null);

const loadYandexMapsApi = (config = {}) => {
  if (window.ymaps?.ready) return Promise.resolve(window.ymaps);
  if (deliveryMapApiPromise) return deliveryMapApiPromise;

  const apiKey = getYandexMapApiKey(config);
  if (!apiKey) return Promise.reject(new Error('Yandex Maps API key is empty'));

  deliveryMapApiPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('wb-yandex-maps-api');

    if (existing) {
      existing.addEventListener('load', () => resolve(window.ymaps), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    const lang = config.lang || 'ru_RU';
    script.id = 'wb-yandex-maps-api';
    script.async = true;
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${encodeURIComponent(apiKey)}&lang=${encodeURIComponent(lang)}`;
    script.addEventListener('load', () => resolve(window.ymaps), { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.appendChild(script);
  });

  return deliveryMapApiPromise;
};

const setPointCardText = (rootElement, selector, value) => {
  const node = rootElement.querySelector(selector);
  if (node && value) node.textContent = value;
};

const updateMapPointCard = (modal, point = {}) => {
  const card = modal.querySelector('.wb-point-card--map');
  if (!card) return;

  setPointCardText(card, '[data-wb-point-name]', point.title || point.service || 'CDEK');
  setPointCardText(card, '[data-wb-point-address]', point.address);
  setPointCardText(card, '[data-wb-point-price]', point.price);
  setPointCardText(card, '[data-wb-point-term]', point.term);

  const route = card.querySelector('[data-wb-point-route]');
  if (route && point.routeUrl) route.href = point.routeUrl;
};

const updatePointDetailView = (modal, point = {}) => {
  const detail = modal.querySelector('[data-wb-point-detail]');
  if (!detail) return;

  setPointCardText(detail, '[data-wb-point-detail-address]', point.address);
  setPointCardText(detail, '[data-wb-point-detail-price]', point.price);
  setPointCardText(detail, '[data-wb-point-detail-term]', point.term);

  const route = detail.querySelector('[data-wb-point-detail-route]');
  if (route && point.routeUrl) route.href = point.routeUrl;
};

const updatePointDetailViewFromCard = (modal, card) => {
  if (!(card instanceof Element)) return;

  const summaryItems = Array.from(card.querySelectorAll('.wb-point-card__summary dd'));
  const route = card.querySelector('.wb-point-card__route');

  updatePointDetailView(modal, {
    address: card.querySelector('.wb-point-card__address')?.textContent.trim(),
    price: summaryItems[0]?.textContent.trim(),
    term: summaryItems[1]?.textContent.trim(),
    routeUrl: route?.getAttribute('href')
  });
};

const showMapPointCard = (modal, point = {}) => {
  updateMapPointCard(modal, point);
  modal.classList.add('is-map-point-open');
};

const hideMapPointCard = (modal) => {
  modal.classList.remove('is-map-point-open');

  const shell = modal.querySelector('[data-wb-yandex-map="delivery-points"]');
  shell?.querySelectorAll('[data-wb-yandex-point-marker]').forEach((marker) => {
    marker.classList.remove('is-active');
    marker.removeAttribute('aria-current');
  });
};

const getDeliveryMapPoints = (config = {}) => Array.isArray(config.points)
  ? config.points.filter((point) => Array.isArray(point.coords) && point.coords.length === 2)
  : [];

const getOrCreateYandexMarkerLayer = (shell) => {
  let layer = shell.querySelector('[data-wb-yandex-marker-layer]');
  if (layer) return layer;

  layer = document.createElement('div');
  layer.className = 'wb-yandex-point-marker-layer';
  layer.dataset.wbYandexMarkerLayer = '';
  shell.appendChild(layer);
  return layer;
};

const getYandexMapPixelPoint = (map, canvas, coords) => {
  if (!map?.converter || !canvas || !Array.isArray(coords)) return null;

  try {
    const projection = map.options?.get?.('projection') || window.ymaps?.projection?.sphericalMercator;
    const zoom = typeof map.getZoom === 'function' ? map.getZoom() : 10;
    const globalPoint = projection?.toGlobalPixels?.(coords, zoom);
    const pagePoint = globalPoint && typeof map.converter.globalToPage === 'function'
      ? map.converter.globalToPage(globalPoint)
      : null;

    if (!Array.isArray(pagePoint)) return null;

    const canvasRect = canvas.getBoundingClientRect();

    return [
      pagePoint[0] - window.pageXOffset - canvasRect.left,
      pagePoint[1] - window.pageYOffset - canvasRect.top
    ];
  } catch (error) {
    return null;
  }
};

const getFallbackMarkerPosition = (points, point) => {
  const bounds = getYandexMapBounds(points);

  if (!bounds || !Array.isArray(point?.coords)) {
    return [50, 50];
  }

  const [[minLat, minLon], [maxLat, maxLon]] = bounds;
  const latRange = Math.max(maxLat - minLat, 0.000001);
  const lonRange = Math.max(maxLon - minLon, 0.000001);
  const lat = Number(point.coords[0]);
  const lon = Number(point.coords[1]);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return [50, 50];
  }

  const x = 14 + ((lon - minLon) / lonRange) * 72;
  const y = 14 + ((maxLat - lat) / latRange) * 72;

  return [
    Math.max(8, Math.min(92, x)),
    Math.max(12, Math.min(88, y))
  ];
};

const setFallbackMarkerPosition = (marker, points, point) => {
  const [left, top] = getFallbackMarkerPosition(points, point);
  marker.hidden = false;
  marker.style.left = `${left}%`;
  marker.style.top = `${top}%`;
};

const positionYandexMarkerButtons = (shell) => {
  const map = shell._wbYandexMap;
  const canvas = shell.querySelector('[data-wb-yandex-map-canvas]');
  const layer = shell.querySelector('[data-wb-yandex-marker-layer]');
  const points = Array.isArray(shell._wbYandexPoints) ? shell._wbYandexPoints : [];

  if (!layer) return;

  layer.querySelectorAll('[data-wb-yandex-point-marker]').forEach((marker) => {
    const pointIndex = Number(marker.dataset.wbYandexPointIndex);
    const point = points[pointIndex] || null;

    if (!point) {
      marker.hidden = true;
      return;
    }

    const pixelPoint = map && canvas ? getYandexMapPixelPoint(map, canvas, point.coords) : null;

    if (!pixelPoint) {
      setFallbackMarkerPosition(marker, points, point);
      return;
    }

    marker.hidden = false;
    marker.style.left = `${pixelPoint[0]}px`;
    marker.style.top = `${pixelPoint[1]}px`;
  });
};

const requestYandexMarkerPositionUpdate = (shell) => {
  window.requestAnimationFrame(() => positionYandexMarkerButtons(shell));
};

const setActiveYandexMarker = (shell, activeMarker) => {
  shell.querySelectorAll('[data-wb-yandex-point-marker]').forEach((marker) => {
    const isActive = marker === activeMarker;
    marker.classList.toggle('is-active', isActive);

    if (isActive) {
      marker.setAttribute('aria-current', 'true');
      return;
    }

    marker.removeAttribute('aria-current');
  });
};

const openYandexPointFromMarker = (modal, shell, marker) => {
  const pointIndex = Number(marker?.dataset.wbYandexPointIndex);
  const point = Array.isArray(shell._wbYandexPoints) ? shell._wbYandexPoints[pointIndex] : null;

  if (!point) return false;

  setActiveYandexMarker(shell, marker);

  if (isMobilePointModal()) {
    updatePointDetailView(modal, point);
    setPointDetailView(modal, true);
    return true;
  }

  showMapPointCard(modal, point);
  return true;
};

const renderYandexMarkerLayer = (modal, shell, points, markerIconSrc, options = {}) => {
  const layer = getOrCreateYandexMarkerLayer(shell);
  layer.innerHTML = '';
  layer.classList.toggle('is-fallback', Boolean(options.isFallback));

  shell._wbYandexPoints = points;

  points.forEach((point, pointIndex) => {
    const marker = document.createElement('button');
    marker.type = 'button';
    marker.className = 'wb-yandex-point-marker';
    marker.dataset.wbYandexPointMarker = '';
    marker.dataset.wbYandexPointIndex = String(pointIndex);
    marker.setAttribute('aria-label', `Выбрать пункт выдачи ${point.address || point.title || ''}`.trim());

    const image = document.createElement('img');
    image.src = markerIconSrc;
    image.alt = point.service || 'CDEK';
    marker.appendChild(image);

    const activateMarker = (event) => {
      event.preventDefault();
      event.stopPropagation();
      openYandexPointFromMarker(modal, shell, marker);
    };

    marker.addEventListener('click', activateMarker);
    marker.addEventListener('pointerup', activateMarker);

    layer.appendChild(marker);
  });

  requestYandexMarkerPositionUpdate(shell);
};

const bindYandexMarkerPositionEvents = (shell, map, canvas) => {
  if (shell._wbYandexMarkerPositionEventsBound || !map?.events) return;

  const updateMarkers = () => requestYandexMarkerPositionUpdate(shell);
  ['boundschange', 'actiontick', 'actionend', 'sizechange'].forEach((eventName) => {
    map.events.add(eventName, updateMarkers);
  });

  if (window.ResizeObserver && canvas) {
    shell._wbYandexMarkerResizeObserver = new ResizeObserver(updateMarkers);
    shell._wbYandexMarkerResizeObserver.observe(canvas);
  }

  shell._wbYandexMarkerPositionEventsBound = true;
};

const renderFallbackYandexMarkers = (modal, shell, config = {}) => {
  const points = getDeliveryMapPoints(config);
  const markerIconSrc = deliveryMapCdekIconUrl.toString();

  if (!points.length) return;

  renderYandexMarkerLayer(modal, shell, points, markerIconSrc, { isFallback: true });
  if (points[0]) updateMapPointCard(modal, points[0]);
};

const getYandexMapBounds = (points) => {
  if (!points.length) return null;

  const lats = points.map((point) => Number(point.coords[0])).filter(Number.isFinite);
  const lons = points.map((point) => Number(point.coords[1])).filter(Number.isFinite);

  if (!lats.length || !lons.length) return null;

  return [
    [Math.min(...lats), Math.min(...lons)],
    [Math.max(...lats), Math.max(...lons)]
  ];
};

const getYandexMarkerButton = (target) => {
  if (!(target instanceof Element)) return null;
  return target.closest('[data-wb-yandex-point-marker]');
};

const getPointerClientPoint = (event) => {
  const touch = event.changedTouches?.[0] || event.touches?.[0];

  if (touch) {
    return { x: touch.clientX, y: touch.clientY };
  }

  if (Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
    return { x: event.clientX, y: event.clientY };
  }

  return null;
};

const isYandexMarkerControlTarget = (target) => target instanceof Element
  && Boolean(target.closest('.wb-point-modal__filters, .wb-point-card--map, .wb-point-modal__search--map, .wb-point-modal__map-filter'));

const getYandexMarkerButtonByPointer = (shell, event) => {
  const pointer = getPointerClientPoint(event);
  if (!pointer) return null;

  return Array.from(shell.querySelectorAll('[data-wb-yandex-point-marker]')).find((marker) => {
    if (marker.hidden) return false;

    const rect = marker.getBoundingClientRect();
    const hitPadding = 8;

    return pointer.x >= rect.left - hitPadding
      && pointer.x <= rect.right + hitPadding
      && pointer.y >= rect.top - hitPadding
      && pointer.y <= rect.bottom + hitPadding;
  }) || null;
};

const handleYandexMarkerActivate = (modal, shell, event) => {
  const directMarker = getYandexMarkerButton(event.target);

  if (!directMarker && isYandexMarkerControlTarget(event.target)) return false;

  const marker = directMarker || getYandexMarkerButtonByPointer(shell, event);
  if (!marker) return false;

  if (!openYandexPointFromMarker(modal, shell, marker)) return false;

  event.preventDefault();
  event.stopPropagation();
  return true;
};

const showYandexMapCanvas = (shell) => {
  const canvas = shell.querySelector('[data-wb-yandex-map-canvas]');
  if (!canvas) return null;

  canvas.hidden = false;
  shell.classList.add('is-yandex-ready');
  return canvas;
};

const renderYandexDeliveryMap = (modal, shell, config = {}) => {
  if (shell._wbYandexMap && window.ymaps?.Map) {
    shell._wbYandexMap.container?.fitToViewport?.();
    requestYandexMarkerPositionUpdate(shell);
    return;
  }

  if (!window.ymaps?.Map) return;

  const canvas = showYandexMapCanvas(shell);
  if (!canvas) return;

  const points = getDeliveryMapPoints(config);
  const center = Array.isArray(config.center) ? config.center : [55.755864, 37.617698];
  const zoom = Number(config.zoom) || 10;
  const markerIconSrc = deliveryMapCdekIconUrl.toString();
  const map = new window.ymaps.Map(canvas, {
    center,
    zoom,
    controls: []
  }, {
    suppressMapOpenBlock: true,
    yandexMapDisablePoiInteractivity: true
  });

  map.behaviors.disable('scrollZoom');
  shell._wbYandexMap = map;

  renderYandexMarkerLayer(modal, shell, points, markerIconSrc, { isFallback: false });
  bindYandexMarkerPositionEvents(shell, map, canvas);

  if (points[0]) updateMapPointCard(modal, points[0]);

  const bounds = getYandexMapBounds(points);
  if (bounds) {
    const setBoundsResult = map.setBounds(bounds, {
      checkZoomRange: true,
      zoomMargin: 90
    });

    if (typeof setBoundsResult?.then === 'function') {
      setBoundsResult.then(() => {
        map.container?.fitToViewport?.();
        requestYandexMarkerPositionUpdate(shell);
      });
    } else {
      requestYandexMarkerPositionUpdate(shell);
    }
  } else {
    requestYandexMarkerPositionUpdate(shell);
  }

  window.setTimeout(() => {
    map.container?.fitToViewport?.();
    requestYandexMarkerPositionUpdate(shell);
  }, 120);
};

const ensureYandexDeliveryMap = (modal) => {
  const shell = modal.querySelector('[data-wb-yandex-map="delivery-points"]');
  if (!shell || shell.dataset.wbYandexMapState === 'ready' || shell.dataset.wbYandexMapState === 'loading') return;

  shell.dataset.wbYandexMapState = 'loading';

  fetchYandexMapConfig(shell)
    .then((config) => {
      if (!config) throw new Error('Delivery map config is empty');

      shell._wbYandexMapConfig = config;

      if (!getYandexMapApiKey(config)) {
        renderFallbackYandexMarkers(modal, shell, config);
        shell.dataset.wbYandexMapState = 'fallback';
        return null;
      }

      return loadYandexMapsApi(config).then((ymaps) => new Promise((resolve) => {
        ymaps.ready(() => resolve(config));
      }));
    })
    .then((config) => {
      if (!config) return;

      renderYandexDeliveryMap(modal, shell, config);
      shell.dataset.wbYandexMapState = shell._wbYandexMap ? 'ready' : 'fallback';
    })
    .catch(() => {
      if (shell._wbYandexMapConfig) {
        renderFallbackYandexMarkers(modal, shell, shell._wbYandexMapConfig);
      }
      shell.dataset.wbYandexMapState = 'fallback';
    });
};

const requestYandexMapRender = (modal) => {
  window.requestAnimationFrame(() => {
    window.setTimeout(() => ensureYandexDeliveryMap(modal), 80);
  });
};

const setPointFiltersOpen = (modal, isOpen) => {
  placePointFilters(modal);
  modal.classList.toggle('is-filter-open', isOpen);
  modal.querySelectorAll('[data-wb-point-filter-toggle]').forEach((toggle) => {
    toggle.setAttribute('aria-expanded', String(isOpen));
  });
};

const setPointDetailView = (modal, isOpen) => {
  const detail = modal.querySelector('[data-wb-point-detail]');

  modal.classList.toggle('is-detail-view', isOpen);
  document.body.classList.toggle('wb-point-modal-detail-open', isOpen);

  if (detail) {
    detail.setAttribute('aria-hidden', String(!isOpen));

    if (isOpen) {
      detail.scrollTop = 0;
      detail.querySelector('[data-wb-point-detail-scroll]')?.scrollTo({ top: 0 });
    }
  }

  if (isOpen) {
    setPointFiltersOpen(modal, false);
  }
};

const getPointModalTrigger = (target) => {
  if (!(target instanceof Element)) return null;
  return target.closest('[data-wb-point-modal-open]');
};

const relocatePointModalToBody = (modal) => {
  if (!(modal instanceof Element)) return modal;

  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  return modal;
};

const getPrimaryPointModal = () => {
  const modals = Array.from(document.querySelectorAll('[data-wb-point-modal]'));
  const modal = modals[0] || null;

  modals.slice(1).forEach((duplicate) => duplicate.remove());

  return modal ? relocatePointModalToBody(modal) : null;
};

const initDeliveryPointModal = () => {
  const modal = getPrimaryPointModal();
  if (!modal) return;

  setPointModalView(modal, modal.dataset.wbPointViewCurrent || 'map');

  document.addEventListener('click', (event) => {
    const trigger = getPointModalTrigger(event.target);
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    const card = trigger.closest('[data-wb-delivery-card]');
    const input = card?.querySelector('input[type="radio"]');
    if (input && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    openPointModal(modal, trigger.dataset.wbPointOpenView || 'map');
  }, true);

  document.addEventListener('keydown', (event) => {
    const trigger = getPointModalTrigger(event.target);
    if (!trigger || (event.key !== 'Enter' && event.key !== ' ')) return;

    event.preventDefault();
    trigger.click();
  }, true);

  modal.querySelectorAll('[data-bs-dismiss="modal"]').forEach((button) => {
    button.addEventListener('click', (event) => {
      if (getPointModalInstance(modal)) return;

      event.preventDefault();
      closePointModal(modal);
      setPointDetailView(modal, false);
    });
  });

  modal.querySelectorAll('[data-wb-point-view]').forEach((button) => {
    button.addEventListener('click', () => {
      setPointModalView(modal, button.dataset.wbPointView);
    });
  });

  modal.querySelectorAll('[data-wb-point-filter-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      setPointFiltersOpen(modal, !modal.classList.contains('is-filter-open'));
    });
  });

  modal.querySelectorAll('[data-wb-point-filter-close]').forEach((button) => {
    button.addEventListener('click', () => {
      setPointFiltersOpen(modal, false);
    });
  });

  modal.querySelectorAll('.wb-point-modal__filter-services .btn, .wb-point-modal__filter-chips .btn').forEach((button) => {
    const setActive = (isActive) => {
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    };

    setActive(button.classList.contains('is-active') || button.getAttribute('aria-pressed') === 'true');

    button.addEventListener('click', () => {
      setActive(!button.classList.contains('is-active'));
    });
  });

  modal.querySelectorAll('.wb-point-modal__filter-check .form-check-input').forEach((input) => {
    const label = input.closest('.wb-point-modal__filter-check');
    const syncCheckedState = () => {
      label?.classList.toggle('is-checked', input.checked);
    };

    input.addEventListener('change', syncCheckedState);
    syncCheckedState();
  });

  modal.querySelectorAll('[data-wb-point-detail-open]').forEach((button) => {
    button.addEventListener('click', () => {
      const card = button.closest('[data-wb-point-card]');
      if (isMobilePointModal()) {
        updatePointDetailViewFromCard(modal, card);
        setPointDetailView(modal, true);
        return;
      }

      card?.classList.add('is-expanded');
    });
  });

  modal.querySelectorAll('[data-wb-point-card-close]').forEach((button) => {
    button.addEventListener('click', () => hideMapPointCard(modal));
  });

  modal.querySelectorAll('[data-wb-point-card-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const card = button.closest('[data-wb-point-card]');
      card?.classList.toggle('is-expanded');
    });
  });

  modal.querySelector('[data-wb-point-detail-back]')?.addEventListener('click', () => {
    setPointDetailView(modal, false);
  });

  modal.querySelectorAll('[data-wb-point-select]').forEach((button) => {
    button.addEventListener('click', () => {
      setPointDetailView(modal, false);
      closePointModal(modal);
    });
  });

  modal.addEventListener('shown.bs.modal', () => {
    requestYandexMapRender(modal);
  });

  modal.addEventListener('hidden.bs.modal', () => {
    setPointDetailView(modal, false);
    setPointFiltersOpen(modal, false);
    hideMapPointCard(modal);
    document.body.classList.remove('wb-point-modal-open', 'wb-point-modal-detail-open');
  });

  window.addEventListener('resize', () => placePointFilters(modal));
};

const initDadataHooks = () => {
  document.querySelectorAll('[data-wb-dadata]').forEach((input) => {
    input.addEventListener('input', () => {
      input.dispatchEvent(new CustomEvent('wb:dadata-input', {
        bubbles: true,
        detail: {
          type: input.dataset.wbDadata,
          value: input.value
        }
      }));
    });
  });
};

if (root) {
  initFields();
  initSelects();
  initRadioCards();
  initRegionConfirm();
  initStepValidation();
  initCollapses();
  initDadataHooks();
  initDeliveryPointModal();
}
